import { Plus, Sparkles, Workflow } from "lucide-react";
import * as React from "react";

import { type WorkflowMeta, workflowsApi } from "@/api/workflows";
import { AIWorkflowSuggesterDialog } from "@/components/admin/AIWorkflowSuggesterDialog";
import { EmptyWorkflows } from "@/components/illustrations/EmptyWorkflows";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { LoadingCards } from "@/components/ui/loading-cards";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

export function WorkflowsPage(): JSX.Element {
  const navigate = useRouteStore((s) => s.navigate);
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<WorkflowMeta[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [code, setCode] = React.useState("");
  const [name, setName] = React.useState("");
  const [showArchived, setShowArchived] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await workflowsApi.list(showArchived);
      setItems(list);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [showArchived, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreate = async (): Promise<void> => {
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(code)) {
      pushToast({ variant: "warning", title: "Code phải snake_case" });
      return;
    }
    if (!name) {
      pushToast({ variant: "warning", title: "Cần name" });
      return;
    }
    try {
      await workflowsApi.create({
        code,
        name,
        graph: {
          code,
          title: name,
          entry: "start",
          nodes: [
            {
              id: "start",
              type: "trigger",
              label: "Trigger",
              position: { x: 200, y: 100 },
            },
            {
              id: "finish",
              type: "end",
              label: "End",
              position: { x: 200, y: 250 },
            },
          ],
          edges: [{ from: "start", to: "finish" }],
        },
      });
      pushToast({ variant: "success", title: `Đã tạo ${code}` });
      setCreating(false);
      setCode("");
      setName("");
      navigate("admin.workflows.edit", code);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Tạo thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  // Group by code → keep latest version + show published flag
  const byCode: Record<string, WorkflowMeta[]> = {};
  for (const w of items) {
    (byCode[w.code] ??= []).push(w);
  }
  const groups = Object.entries(byCode);

  return (
    <div className="flex-1 flex flex-col">
      <header className="h-12 shrink-0 flex items-center px-6 gap-3 border-b bg-background/50">
        <h1 className="font-semibold">Workflow Designer</h1>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Archived
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAiOpen(true)}
            title="AI gen workflow từ mô tả"
          >
            <Sparkles className="size-4 mr-1" /> AI suggest
          </Button>
          <Button size="sm" variant="brand" onClick={() => setCreating(true)}>
            <Plus className="size-4 mr-1" /> Tạo workflow
          </Button>
        </div>
      </header>
      <AIWorkflowSuggesterDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onCreated={refresh}
      />

      <main className="flex-1 max-w-5xl w-full mx-auto p-6 space-y-4 overflow-y-auto">
        {creating ? (
          <Card>
            <CardContent className="p-4 space-y-3">
              <h3 className="font-medium">Tạo workflow mới</h3>
              <div>
                <label className="text-xs font-medium block mb-1">
                  Code (snake_case)
                </label>
                <Input
                  value={code}
                  onChange={(e) =>
                    setCode(
                      e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                    )
                  }
                  placeholder="pr_approval_flow"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="PR Approval Flow"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreating(false)}>
                  Huỷ
                </Button>
                <Button onClick={() => void handleCreate()}>Tạo + Mở Designer</Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {loading ? (
          <LoadingCards count={4} />
        ) : groups.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <EmptyState
                hero={<EmptyWorkflows size={140} />}
                title="Chưa có workflow nào"
                description="Tạo workflow để orchestrate multi-step process: trigger → form → branch → human approve → notify."
                action={
                  <Button variant="brand" onClick={() => setCreating(true)}>
                    <Plus className="size-4 mr-1" /> Tạo workflow đầu tiên
                  </Button>
                }
                hint="💡 Workflow phổ biến: PR Approval Flow, Quotation Flow, Customer Onboarding."
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {groups.map(([code, versions]) => {
              const latest = versions[0];
              if (!latest) return null;
              const published = versions.find((v) => v.status === "published");
              return (
                <Card key={code}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <Workflow className="size-5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{latest.name}</span>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {code}
                        </code>
                        {published ? (
                          <Badge variant="default" className="text-[10px]">
                            v{published.version} live
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">
                            Chưa publish
                          </Badge>
                        )}
                      </div>
                      {latest.description ? (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {latest.description}
                        </p>
                      ) : null}
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Latest v{latest.version} ({latest.status}) ·{" "}
                        {versions.length} version(s)
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate("admin.workflows.edit", code)}
                    >
                      Edit
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
